import { useMemo, useReducer, useRef } from 'react';
import _ from 'lodash';
import { gql, useMutation, useQuery } from '@apollo/client';
import { getSectionCropMeta, SECTION_CROP_META_VERSION } from '../utils/meta';
import { DeleteSectionMutation, UpsertSectionMutation } from '../mutations';
import useUndoRedo from './useUndoRedo';
import {
  mergeSections,
  newDefaultSection,
  writeSectionS3Geodata,
} from '../utils/field';
import { SectionGroupsQuery } from '../queries';
import { sectionHasGeodata } from '../utils/sections';
import { addPointsAndRows } from '../utils/points-and-rows/addPointsAndRows';
import { overrideMatchesSection } from '../utils/points-and-rows/rows';
import { showError } from '../utils/toast';

const SectionSignedPutUrlQuery = gql`
  query SectionSignedPutUrl($id: ID!, $farmId: ID!) {
    sectionSignedPutUrl(id: $id, farmId: $farmId)
  }
`;

const initialFieldState = {};

const fieldsReducer = (sections, action) => {
  switch (action.type) {
    case 'bulkAdd': {
      // Don't re-add duplicate sections
      const existingIds = Object.values(sections).map((s) => s.id);
      const filteredSections = action.sections
        .filter(({ id }) => !id || !existingIds.includes(id))
        // Initialize sections to shown
        .map((s) => ({ ...s, shown: true }));
      return { ...sections, ..._.keyBy(filteredSections, 'id') };
    }
    case 'remove': {
      const newSections = { ...sections };
      delete newSections[action.id];
      return newSections;
    }
    case 'update': {
      return {
        ...sections,
        [action.section.id]: action.section,
      };
    }
    case 'reset': {
      return initialFieldState;
    }
    case 'toggleShown': {
      return {
        ...sections,
        [action.id]: {
          ...sections[action.id],
          shown: !sections[action.id].shown,
        },
      };
    }
    case 'bulkSetShown': {
      const hiddenSections = action.sectionIds.map((id) => ({
        ...sections[id],
        shown: action.shown,
      }));
      return { ...sections, ..._.keyBy(hiddenSections, 'id') };
    }
    default:
      throw new Error(`Invalid type: ${action.type}.`);
  }
};

const useFieldSections = ({
  cropNameToId,
  cropVarietyIdToCropId,
  cropMap,
  inRowPatternsMap,
}) => {
  const pendingSectionMap = useRef({});
  const mutationPromiseMap = useRef({});
  const updatedAtMap = useRef({});

  const {
    undo,
    redo,
    update: updateUndoState,
    reset: resetUndoState,
    canUndo,
    canRedo,
  } = useUndoRedo();

  // STATE
  const [sections, dispatch] = useReducer(fieldsReducer, initialFieldState);

  // QUERIES
  const { refetch: refetchSignedPutUrl } = useQuery(SectionSignedPutUrlQuery, {
    skip: true,
  });

  // MUTATIONS
  const [upsertRemoteSection] = useMutation(UpsertSectionMutation);
  const [deleteRemoteSection] = useMutation(DeleteSectionMutation);

  // UPDATES
  const deleteRemote = (id) => {
    pendingSectionMap.current[id] = null;
    const updatedAt = updatedAtMap.current[id] || sections[id]?.updatedAt;
    const { farmId } = sections[id] || {};
    // If there is no updatedAt timestamp, this section was never uploaded,
    // so we can just delete it locally.
    if (!updatedAt) return;
    mutationPromiseMap.current[id] = deleteRemoteSection({
      variables: { input: { id, updatedAt } },
      refetchQueries: [{ query: SectionGroupsQuery, variables: { farmId } }],
      awaitRefetchQueries: true,
    })
      .then((response) => {
        const { result } = response.data?.deleteSection || {};
        mutationPromiseMap.current[id] = null;
        if (!result) {
          showError('Failed to delete section. Reload and try again.');
        }
      })
      .catch((error) => {
        showError(error);
        mutationPromiseMap.current[id] = null;
      });
  };

  const stringifyAndUpsertRemote = ({ section, uploadGeodata = false }) => {
    pendingSectionMap.current[section.id] = null;
    const cropMetas = getSectionCropMeta(section, {
      cropNameToId,
      cropVarietyIdToCropId,
      inRowPatternsMap,
    });
    const cropId = section.crop?.id || null;
    const primaryCropVarietyId = section.primaryCropVariety?.id || null;
    const primaryInRowPatternId = section.primaryInRowPattern?.id || null;
    // Write points and rows to S3
    mutationPromiseMap.current[section.id] = writeSectionS3Geodata(
      section,
      uploadGeodata,
      refetchSignedPutUrl,
    )
      .then((s3VersionId) => {
        // Upsert section to server
        const updatedSection = {
          ...section,
          sectionCropMetaVersion: SECTION_CROP_META_VERSION,
          cropMetas,
          cropId,
          primaryCropVarietyId,
          primaryInRowPatternId,
          rowOverrides: JSON.stringify(section.rowOverrides || []),
          startYear: section.startYear,
          updatedAt: updatedAtMap.current[section.id] || section.updatedAt,
          // Do not update the S3 version id if no request is made to S3
          s3VersionId: s3VersionId || undefined,
          // Remove extraneous section variables
          points: undefined,
          rows: undefined,
          allPoints: undefined,
          crop: undefined,
          primaryCropVariety: undefined,
          primaryInRowPattern: undefined,
          signedUrl: undefined,
        };
        return upsertRemoteSection({
          variables: updatedSection,
        });
      })
      .then((response) => {
        const { errors, result } = response.data?.upsertSection || {};
        if (errors) {
          // Handle validation errors from the server
          showError(errors);
        } else {
          const updatedAt = result?.updatedAt;
          updatedAtMap.current[section.id] =
            updatedAt || updatedAtMap.current[section.id];
        }
      })
      .catch((error) => {
        // Handle top level errors
        showError(error);
      })
      .finally(() => {
        mutationPromiseMap.current[section.id] = null;
        const pendingUpdate = pendingSectionMap.current[section.id];
        if (pendingUpdate) {
          if (pendingUpdate.delete) {
            deleteRemote(pendingUpdate.id);
          } else {
            stringifyAndUpsertRemote(pendingSectionMap.current[section.id]);
          }
        }
      });
  };

  const setFieldSection = (id, { section, uploadGeodata }) => {
    dispatch({ type: 'update', section });
    if (!sectionHasGeodata(section)) return;
    if (mutationPromiseMap.current[id]) {
      // Mark the section dirty if there is already an update in progress.
      const { uploadGeodata: pendingUploadGeodata } =
        pendingSectionMap.current[id] || {};
      pendingSectionMap.current[id] = {
        section,
        uploadGeodata: uploadGeodata || pendingUploadGeodata,
      };
    } else {
      // Send an update if there isn't one in progress.
      stringifyAndUpsertRemote({ section, uploadGeodata });
    }
  };

  const updateFieldSection = (id, update) => {
    const oldSection = sections[id];
    if (!oldSection) {
      console.error("Can't update a section that no longer exists.");
      return;
    }
    // Only add the old section to the undo queue if it has geodata.
    if (sectionHasGeodata(oldSection)) updateUndoState(oldSection);
    const { section, uploadGeodata } = mergeSections(oldSection, update, {
      cropMap,
      cropNameToId,
      inRowPatternsMap,
    });
    setFieldSection(id, { section, uploadGeodata });
  };

  const setRowOverrides = (sectionId, rowIndexes, override) => {
    const rowOverrides = [...(sections[sectionId]?.rowOverrides || [])];
    rowIndexes.forEach((rowIndex) => {
      rowOverrides[rowIndex] = override;
    });
    const update = { rowOverrides };
    updateFieldSection(sectionId, update);
  };

  const updateRowOverrides = (sectionId, rowIndexes, override) => {
    const rowOverrides = [...(sections[sectionId]?.rowOverrides || [])];
    rowIndexes.forEach((rowIndex) => {
      const updatedOverride = {
        ...(rowOverrides[rowIndex] || {}),
        ...override,
      };
      if (overrideMatchesSection(updatedOverride, sections[sectionId])) {
        rowOverrides[rowIndex] = undefined;
        return;
      }
      rowOverrides[rowIndex] = updatedOverride;
    });
    const update = { rowOverrides };
    updateFieldSection(sectionId, update);
  };

  const clearRowOverrides = (sectionId, rowIndexes) =>
    setRowOverrides(sectionId, rowIndexes, undefined);

  const removeFieldSection = (id) => {
    dispatch({ type: 'remove', id });
    if (mutationPromiseMap.current[id]) {
      // Mark the section dirty if there is already an update in progress.
      pendingSectionMap.current[id] = { id, delete: true };
    } else {
      // Send an update if there isn't one in progress.
      deleteRemote(id);
    }
  };

  const addFieldSection = (values = {}) => {
    // Add points in rows in case the section already has a fieldFeature
    const newSection = addPointsAndRows(newDefaultSection(values), {
      cropMap,
      cropNameToId,
      inRowPatternsMap,
    });
    dispatch({ type: 'bulkAdd', sections: [newSection] });
    if (sectionHasGeodata(newSection)) {
      stringifyAndUpsertRemote({ section: newSection, uploadGeodata: true });
    }
    // Return the newly created section
    return newSection;
  };

  const addFieldSections = (
    newSections,
    { uploadMetadata = true, uploadGeodata = true } = {},
  ) => {
    if (uploadMetadata) {
      newSections.forEach((section) => {
        stringifyAndUpsertRemote({ section, uploadGeodata });
      });
    }
    dispatch({ type: 'bulkAdd', sections: newSections });
  };

  const getNameForDuplicateSection = (originalName) => {
    const nameSet = new Set(
      Object.values(sections).map((section) => section.name),
    );
    let i = 1;
    while (nameSet.has(`${originalName} (${i})`)) {
      i += 1;
    }
    return `${originalName} (${i})`;
  };

  const duplicateFieldSection = (id) => {
    if (!sections[id]) {
      console.error("Can't duplicate a section that does not exist.");
      return null;
    }
    const duplicateSection = {
      ...sections[id],
      name: getNameForDuplicateSection(sections[id].name),
    };
    delete duplicateSection.id;
    delete duplicateSection.signedUrl;
    return addFieldSection(duplicateSection);
  };

  const resetFields = () => dispatch({ type: 'reset' });

  const toggleSectionShown = (id) => dispatch({ type: 'toggleShown', id });

  const bulkHideSections = (sectionsToHide) =>
    dispatch({
      type: 'bulkSetShown',
      sectionIds: sectionsToHide,
      shown: false,
    });

  const bulkShowSections = (sectionsToShow) =>
    dispatch({ type: 'bulkSetShown', sectionIds: sectionsToShow, shown: true });

  // Undo/redo
  const onDo = (doFn) => (sectionId) => {
    const oldSection = sections[sectionId];
    const newSection = doFn(oldSection);
    if (!newSection) return {};
    if (newSection.id !== sectionId) {
      console.error(
        `Section id mismatch: ${sectionId} doesn't match ${newSection.id}`,
      );
      return {};
    }
    // Merge the two sections to determine if the rows or points have changed
    setFieldSection(
      sectionId,
      mergeSections(oldSection, newSection, {
        cropMap,
        cropNameToId,
        inRowPatternsMap,
      }),
    );
    return { newSection, oldSection };
  };

  const fieldSections = useMemo(() => Object.values(sections), [sections]);

  return {
    fieldSections,
    addFieldSection, // Add a new default field section.
    addFieldSections, // Bulk add field sections.
    duplicateFieldSection, // Duplicate a field section by id.
    removeFieldSection, // Remove a field section by id.
    updateFieldSection, // Update a field section by id.
    resetFields, // Reset state to initial state
    toggleSectionShown, // Sets a section hidden or shown on the map.
    bulkHideSections, // hides a list of existing sections
    bulkShowSections, // shows a list of existing sections
    // Row Overrides
    setRowOverrides,
    updateRowOverrides,
    clearRowOverrides,
    sectionsHash: sections,
    // Undo/redo
    undoRedo: {
      onUndo: onDo(undo),
      onRedo: onDo(redo),
      reset: resetUndoState,
      canUndo,
      canRedo,
    },
  };
};

export default useFieldSections;
